import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { EntityResultsPageTransport } from '../transports/entity-results-transport'
import comboListingUnitClient from '../clients/combo-listing-unit-client'
import { FormattedPropertyData, PropertyDetails } from '../models/property-details-page/property-details-model'
import useLazyPagination from './lazy-pagination'
import { ScreenContext } from 'react-components'
import { EntitySectionConstant, SECTION_IDS } from '../components/property-details/constants'
import { useRequestUserConfigContext } from './request'
import uniq from 'lodash/uniq'
import { scrollToSection } from '../components/property-details/navigation-bar'
import { usePropertyDetailsContext } from './property-details'

interface UseLoadUnitsState {
    units: FormattedPropertyData[]
    isLoading: boolean
    isError: boolean
    hasMore: boolean
    loadedObjectIds: string[]
}

interface UseLoadUnitsParams {
    listingId: string
    arrival: string | null
    departure: string | null
    adults: number
    childrenAges: string
    filter: string
}

export const useLoadUnits = (
    { listingId, arrival, departure, adults, childrenAges, filter }: UseLoadUnitsParams,
    propertyDetailsFormatter: PropertyDetails,
) => {
    const [state, setState] = useState<UseLoadUnitsState>({
        units: [],
        isLoading: false,
        isError: false,
        hasMore: true,
        loadedObjectIds: [],
    })

    const { isComboListing } = usePropertyDetailsContext()
    const [selectDateIndicator, setSelectDateIndicator] = useState<boolean>(false)
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)

    const { isMobile } = useContext(ScreenContext)
    const user = useRequestUserConfigContext()

    // fetch method which will be pass on lazy pagination hook
    const fetchPage = useCallback(
        async (page: number) => {
            if (!isComboListing) {
                return {
                    items: [],
                    totalItems: 0,
                }
            }

            try {
                const response = await comboListingUnitClient.getUnits({
                    user,
                    params: {
                        listingId,
                        arrival,
                        departure,
                        adults,
                        childrenAges,
                        filter,
                        offset: (page - 1) * EntitySectionConstant.UNIT_PER_PAGE,
                    },
                })

                if (response?.data) {
                    const entityData: EntityResultsPageTransport = response?.data

                    const formattedData: FormattedPropertyData[] = Object.values(entityData.units).map(
                        ({ expose, object }) => propertyDetailsFormatter.getData(expose, object),
                    )

                    return {
                        items: formattedData,
                        totalItems: entityData.totalHits,
                    }
                } else {
                    throw new Error('No unit data available.')
                }
            } catch {
                throw new Error('error occured while fetching unit data')
            }
        },
        [arrival, departure, listingId, adults, childrenAges, filter, propertyDetailsFormatter, user, isComboListing],
    )

    const { cumulativeData, loading, nextPage, hasNextPage, totalItems, error, reset } = useLazyPagination(
        fetchPage,
        EntitySectionConstant.UNIT_PER_PAGE,
    )

    // will run when pagination data chnage
    useEffect(() => {
        if (!isComboListing) {
            return
        }

        setState(prev => ({
            ...prev,
            units: cumulativeData,
            isLoading: loading,
            hasMore: hasNextPage,
            isError: error ? true : false,
            loadedObjectIds: uniq(cumulativeData.map(unit => unit.id).filter(Boolean)),
        }))
    }, [cumulativeData, loading, hasNextPage, isComboListing, error])

    // Handles auto-selecting first unit on fresh load
    useEffect(() => {
        if (!isComboListing) {
            return
        }

        if (cumulativeData.length > 0 && selectedIndex === -1) {
            setSelectedIndex(0)
        }
    }, [cumulativeData, selectedIndex, isComboListing, setSelectedIndex])

    const prevParamsRef = useRef({
        arrival: null as string | null,
        departure: null as string | null,
        listingId: listingId as string,
        adults: adults as number,
        childrenAges: childrenAges as string,
        filter: filter as string,
    })

    // reset the entity data when user changes the date
    useEffect(() => {
        if (!isComboListing) {
            return
        }
        if (arrival === null || departure === null) {
            return
        }

        const hasChanged =
            prevParamsRef.current.arrival !== arrival ||
            prevParamsRef.current.departure !== departure ||
            prevParamsRef.current.listingId !== listingId ||
            prevParamsRef.current.adults !== adults ||
            prevParamsRef.current.childrenAges !== childrenAges ||
            prevParamsRef.current.filter !== filter

        if (hasChanged) {
            prevParamsRef.current = { arrival, departure, listingId, adults, childrenAges, filter }
            setSelectDateIndicator(false)
            scrollToSection(SECTION_IDS.ENTITY_SECTION_ID)
            reset()
            setSelectedIndex(-1)
        }
    }, [arrival, departure, listingId, adults, childrenAges, filter, reset, setSelectedIndex, isComboListing])

    // function which will run on `select date and guests` button click
    const onButtonClick = useCallback(() => {
        if (arrival !== null && departure !== null) {
            return
        }

        if (isMobile) {
            scrollToSection(SECTION_IDS.PRICE_SECTION_ID)
        }
        setSelectDateIndicator(true)
    }, [arrival, departure, isMobile])

    return {
        ...state,
        totalUnits: totalItems,
        loadMoreUnits: nextPage,
        onButtonClick,
        selectDateIndicator,
        selectedIndex,
        setSelectedIndex,
    }
}
